/**
 * Redaktionelle Texte eines Produktmodells.
 *
 * Wie bei der Produktfamilie gibt es hier keine Vererbung -- das Modell ist
 * die Quelle. Dargestellt werden ausschliesslich die Texttypen, die das
 * Modell auf Modellebene zulaesst (`allowsModel`): Langtext und Anwendung
 * beschreiben die Plattform, nicht die einzelne Ausfuehrung
 * (PRODUCT_DATA_MODEL.md 21.15).
 *
 * Diese Liste stammt aus `meta.textTypes` und damit aus der Datenbank. Das
 * Frontend fuehrt KEINE eigene Texttypliste.
 */
import { useCallback, useEffect, useState } from "react";
import type { MetaResponse, ProductText } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { ErrorState, LoadingState } from "./States.tsx";
import { ProductTexts, type TextSlot } from "./ProductTexts.tsx";

export function ModelTexts({ modelId, meta, mayWrite, mayRelease }: {
  modelId: number;
  meta: MetaResponse;
  mayWrite: boolean;
  mayRelease: boolean;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [texts, setTexts] = useState<readonly ProductText[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try { setTexts((await api.modelTexts(modelId)).items); }
    catch (e) { setError(e); }
  }, [modelId]);

  useEffect(() => { setTexts(null); setEditing(null); void load(); }, [load]);

  if (error !== null && !texts) {
    return <div className="texts"><ErrorState message={errorText(i18n, error)} onRetry={load} /></div>;
  }
  if (!texts) return <div className="texts"><LoadingState label={t("texts.loading")} /></div>;

  const byType = new Map(texts.map((x) => [x.type, x]));
  const slots: TextSlot[] = meta.textTypes
    .filter((tt) => tt.allowsModel)
    .map((tt) => ({
      type: tt.code, maxLength: tt.maxLength, text: byType.get(tt.code) ?? null,
      inherited: false, inheritedFrom: null, editable: true, ownAllowed: false,
    }));

  return (
    <div className="texts">
      <h3 className="detail__section">{t("texts.sectionModel")}</h3>
      {/*
        * DER SATZ, DER DIE EBENEN TRENNT.
        *
        * Ein Modelltext sah bisher aus wie der fertige Produkttext -- er
        * hiess „Modelltexte", stand im Modell und erschien an jeder SKU der
        * Baureihe. Er ist aber ein BAUSTEIN: er gilt nur, soweit die Aussage
        * fuer ALLE Ausfuehrungen stimmt. Das steht jetzt da, statt dass man
        * es wissen muss.
        */}
      <p className="texts__basisnote">{t("texts.noteModel")}</p>
      {texts.length === 0 && !mayWrite && <p className="texts__empty">{t("texts.emptyModel")}</p>}
      {/* Ein Produktmodell hat keinen eigenen Supplier-Textbezug -- der
          Herkunftsbezug einer Fassung haengt an der Fassung selbst. */}
      <ProductTexts
        slots={slots} subject={{ kind: "model", id: modelId }}
        mayWrite={mayWrite} mayRelease={mayRelease} supplier={null}
        editing={editing} setEditing={setEditing}
        onChanged={() => { setEditing(null); void load(); }}
        onError={setError}
      />
      {error !== null && <p className="texts__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}
